// Hub aéroport — ex. Geneva Airport Transfer (GVA)
import { Link } from "react-router-dom";
import { ArrowRight, Check, Plane } from "lucide-react";
import {
  FaqBlock, JsonLd, RouteLinks, SeoPage, TrustChips, useHreflang, useSeo, GuideLinks,
} from "@/components/seo/Seo";
import {
  AIRPORTS, BASE_URL, CITIES, LANGS, PLACE_COORDS, S, T, p, routesFrom,
} from "@/lib/seoContent";

export default function AirportHub({ lang, airportId }) {
  const airport = AIRPORTS[airportId];
  const t = T[lang];
  const city = airport ? CITIES[airport.city] : null;
  const path = airport ? p(lang, S.airport[lang], airport.slug[lang]) : `/${lang}`;
  const alternates = airport ? Object.fromEntries(LANGS.map((l) => [l, p(l, S.airport[l], airport.slug[l])])) : null;

  useSeo({ lang, title: airport ? airport.title[lang] : "MILES", description: airport ? airport.meta[lang] : "", path, type: "airport", id: airportId });
  useHreflang(alternates);

  if (!airport) return null;

  const routes = routesFrom(airportId);
  const crumbs = [
    { label: t.home, to: p(lang) },
    { label: { fr: "Transferts aéroport", en: "Airport transfers", de: "Flughafentransfers" }[lang], to: p(lang, S.airport[lang]) },
    { label: airport.name[lang] },
  ];

  const faq = {
    fr: [
      ["Où mon chauffeur m'attend-il à l'aéroport ?", airport.pickup[lang]],
      ["Que se passe-t-il si mon vol est retardé ?", "Nous suivons votre vol et ajustons automatiquement l'heure de prise en charge. Les 60 minutes d'attente après l'atterrissage sont offertes."],
      ["Combien coûte un transfert depuis l'aéroport ?", "Le prix est fixé et affiché avant la réservation — il dépend de la destination et du véhicule. Consultez les trajets ci-dessus pour les prix exacts."],
      ["Taxi ou transfert privé ?", "Le transfert privé MILES est réservé à l'avance à prix fixe : pas de file d'attente, pas de compteur, et votre chauffeur vous attend même si votre vol est en retard."],
    ],
    en: [
      ["Where does my chauffeur wait at the airport?", airport.pickup[lang]],
      ["What happens if my flight is delayed?", "We track your flight and automatically adjust the pickup time. The first 60 minutes of waiting after landing are free."],
      ["How much is a transfer from the airport?", "The price is fixed and shown before booking — it depends on the destination and vehicle. See the routes above for exact prices."],
      ["Taxi or private transfer?", "A MILES private transfer is booked ahead at a fixed price: no queue, no meter, and your chauffeur waits even if your flight is late."],
    ],
    de: [
      ["Wo wartet mein Fahrer am Flughafen?", airport.pickup[lang]],
      ["Was passiert bei Flugverspätung?", "Wir verfolgen Ihren Flug und passen die Abholzeit automatisch an. Die ersten 60 Minuten Wartezeit nach der Landung sind kostenlos."],
      ["Was kostet ein Transfer ab Flughafen?", "Der Preis ist fix und wird vor der Buchung angezeigt — abhängig von Ziel und Fahrzeug. Die genauen Preise stehen bei den Routen oben."],
      ["Taxi oder Privattransfer?", "Der MILES-Privattransfer wird im Voraus zum Fixpreis gebucht: keine Warteschlange, kein Taxameter, und Ihr Fahrer wartet auch bei Verspätung."],
    ],
  }[lang];

  const jsonLd = [
    { "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: crumbs.map((c, i) => ({ "@type": "ListItem", position: i + 1, name: c.label, ...(c.to ? { item: `${BASE_URL}${c.to}` } : {}) })) },
    { "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: faq.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
    { "@context": "https://schema.org", "@type": "Airport", name: airport.name.en, iataCode: airport.iata },
  ];

  return (
    <SeoPage lang={lang} breadcrumbs={crumbs} testId={`seo-airport-${airportId}`}>
      {jsonLd.map((j, i) => <JsonLd key={i} id={`airport-${i}`} data={j} />)}

      <div className="flex items-center gap-4">
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-miles-deep font-mono text-sm font-bold text-white">{airport.iata}</span>
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-miles">{t.airport_of}</p>
      </div>
      <h1 className="mt-4 max-w-3xl text-3xl font-bold leading-tight text-ink sm:text-5xl" data-testid="airport-h1">{airport.h1[lang]}</h1>
      <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate">{airport.intro[lang]}</p>

      <div className="mt-7 flex flex-wrap gap-3">
        <Link to={`/reservation?type=one_way&from=${encodeURIComponent(airport.name.en)}&from_lat=${PLACE_COORDS[airportId][0]}&from_lon=${PLACE_COORDS[airportId][1]}`} data-testid="airport-book-cta"
          className="inline-flex h-12 items-center gap-2 rounded-xl bg-miles px-6 text-sm font-semibold text-white transition-opacity hover:opacity-90">
          {t.book_cta} <ArrowRight className="h-4 w-4" strokeWidth={2} />
        </Link>
        <Link to={p(lang, S.chauffeur[lang], city.slug[lang])} data-testid="airport-city-cta"
          className="inline-flex h-12 items-center gap-2 rounded-xl border border-ink/15 bg-white px-6 text-sm font-semibold text-ink transition-colors hover:border-miles/40">
          {t.chauffeur_in} {city.name[lang]}
        </Link>
      </div>

      <ul className="mt-10 space-y-2.5">
        {airport.facts[lang].map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm text-slate">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" strokeWidth={2} /> {f}
          </li>
        ))}
      </ul>

      <div className="mt-8"><TrustChips lang={lang} ski /></div>

      <section className="mt-14 rounded-2xl border border-ink/10 bg-white p-6 sm:p-8" data-testid="airport-pickup">
        <h2 className="flex items-center gap-2 text-xl font-bold text-ink"><Plane className="h-5 w-5 text-miles" strokeWidth={1.5} /> {t.pickup_title} — {airport.name[lang]}</h2>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate">{airport.pickup[lang]}</p>
      </section>

      <div className="mt-14 space-y-14">
        <RouteLinks title={`${t.popular_from} ${airport.name[lang]}`} routes={routes} lang={lang} testId="airport-routes" />
        <GuideLinks lang={lang} path={path} />
        <FaqBlock lang={lang} faq={faq} />
      </div>
    </SeoPage>
  );
}
